import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@exhousing/design-system";
import {
  ArrowLeft,
  Briefcase,
  FileText,
  Paperclip,
  Plus,
  RefreshCw,
  User,
  UserSearch,
  X,
} from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";

import { StatusBadge } from "@/components/StatusBadge";
import { useContracts } from "@/hooks/api/useContracts";
import { useLeads } from "@/hooks/api/useCrm";
import {
  type InboxEntityType,
  useInboxMessage,
  useLinkMessage,
  useRematchMessage,
  useUnlinkMessage,
} from "@/hooks/api/useInbox";
import { useLandlords, useTenants } from "@/hooks/api/useParties";

type Resolved = { label: string; href?: string };

const ENTITY_META: Record<
  InboxEntityType,
  { icon: typeof User; label: string }
> = {
  contact: { icon: User, label: "Contact" },
  lead: { icon: UserSearch, label: "Lead" },
  contract: { icon: Briefcase, label: "Contract" },
};

export function InboxMessagePage() {
  const { id } = useParams<{ id: string }>();
  const { data: msg, isLoading, isError } = useInboxMessage(id);
  const link = useLinkMessage(id ?? "");
  const unlink = useUnlinkMessage(id ?? "");
  const rematch = useRematchMessage(id ?? "");

  const { data: tenants } = useTenants();
  const { data: landlords } = useLandlords();
  const { data: leads } = useLeads();
  const { data: contracts } = useContracts();

  const [pickerType, setPickerType] = useState<InboxEntityType>("contact");
  const [pickerId, setPickerId] = useState("");

  // Build id → {label, href} maps so a stored link (which only has an id) renders
  // a friendly, clickable reference.
  const { resolve, options } = useMemo(() => {
    const contacts = new Map<string, Resolved>();
    for (const t of tenants ?? [])
      contacts.set(t.contact.id, {
        label: t.contact.display_name,
        href: `/parties/tenants/${t.id}`,
      });
    for (const l of landlords ?? [])
      contacts.set(l.contact.id, {
        label: l.contact.display_name,
        href: `/parties/landlords/${l.id}`,
      });
    const leadMap = new Map<string, Resolved>();
    for (const l of leads ?? [])
      leadMap.set(l.id, {
        label:
          [l.first_name, l.last_name].filter(Boolean).join(" ") ||
          l.email ||
          "Lead",
        href: "/crm",
      });
    const contractMap = new Map<string, Resolved>();
    for (const c of contracts ?? [])
      contractMap.set(c.id, {
        label: c.property_address ?? c.reference ?? "Contract",
        href: `/contracts/${c.id}`,
      });

    const resolve = (type: string, entityId: string): Resolved => {
      const m =
        type === "contact" ? contacts : type === "lead" ? leadMap : contractMap;
      return m.get(entityId) ?? { label: `${entityId.slice(0, 8)}…` };
    };
    const options: Record<InboxEntityType, { id: string; label: string }[]> = {
      contact: [...contacts].map(([id, r]) => ({ id, label: r.label })),
      lead: [...leadMap].map(([id, r]) => ({ id, label: r.label })),
      contract: [...contractMap].map(([id, r]) => ({ id, label: r.label })),
    };
    return { resolve, options };
  }, [tenants, landlords, leads, contracts]);

  if (isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (isError || !msg)
    return <p className="text-sm text-destructive">Message not found.</p>;

  const onAddLink = () => {
    if (!pickerId) return;
    link.mutate(
      { entity_type: pickerType, entity_id: pickerId },
      {
        onSuccess: () => {
          toast.success("Linked");
          setPickerId("");
        },
        onError: (err) => toast.error(err.message),
      },
    );
  };

  const selectClass =
    "h-9 rounded-md border border-input bg-card px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <Link
        to="/inbox"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-4" />
        Inbox
      </Link>

      <header className="space-y-3">
        <div className="flex items-start justify-between gap-4">
          <h1 className="font-display text-2xl font-medium tracking-tight">
            {msg.subject || "(no subject)"}
          </h1>
          <StatusBadge status={msg.match_status} />
        </div>
        <div className="space-y-0.5 text-sm text-muted-foreground">
          <p>
            <span className="text-foreground/70">From</span>{" "}
            {msg.from_name ? `${msg.from_name} · ` : ""}
            {msg.from_addr ?? "—"}
          </p>
          <p>
            <span className="text-foreground/70">To</span>{" "}
            {msg.to_addrs.join(", ") || "—"}
          </p>
          {msg.cc_addrs.length > 0 && (
            <p>
              <span className="text-foreground/70">Cc</span> {msg.cc_addrs.join(", ")}
            </p>
          )}
          {msg.sent_at && (
            <p>{new Date(msg.sent_at).toLocaleString("nl-NL")}</p>
          )}
        </div>
      </header>

      {/* Filed under — who this message is about, with manual override. */}
      <Card>
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">Filed under</CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              rematch.mutate(undefined, {
                onSuccess: () => toast.success("Re-matched"),
                onError: (err) => toast.error(err.message),
              })
            }
            disabled={rematch.isPending}
          >
            <RefreshCw
              className={rematch.isPending ? "size-4 animate-spin" : "size-4"}
            />
            Re-run matching
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {msg.links.length > 0 ? (
            <ul className="flex flex-wrap gap-2">
              {msg.links.map((l) => {
                const r = resolve(l.entity_type, l.entity_id);
                const Icon =
                  ENTITY_META[l.entity_type as InboxEntityType]?.icon ?? User;
                return (
                  <li key={l.id}>
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card py-1 pl-2.5 pr-1 text-sm">
                      <Icon className="size-3.5 text-muted-foreground" />
                      {r.href ? (
                        <Link to={r.href} className="hover:text-primary">
                          {r.label}
                        </Link>
                      ) : (
                        <span>{r.label}</span>
                      )}
                      {!l.is_primary && (
                        <Badge tone="neutral" className="ml-0.5">
                          via {l.entity_type}
                        </Badge>
                      )}
                      <button
                        type="button"
                        onClick={() =>
                          unlink.mutate(l.id, {
                            onError: (err) => toast.error(err.message),
                          })
                        }
                        className="ml-0.5 rounded-full p-0.5 text-muted-foreground hover:bg-muted hover:text-danger"
                        aria-label="Remove link"
                      >
                        <X className="size-3.5" />
                      </button>
                    </span>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">
              Not linked to anyone yet — file it against the right person below.
            </p>
          )}

          <div className="flex flex-wrap items-center gap-2 border-t border-border pt-4">
            <select
              value={pickerType}
              onChange={(e) => {
                setPickerType(e.target.value as InboxEntityType);
                setPickerId("");
              }}
              className={selectClass}
            >
              <option value="contact">Contact</option>
              <option value="lead">Lead</option>
              <option value="contract">Contract</option>
            </select>
            <select
              value={pickerId}
              onChange={(e) => setPickerId(e.target.value)}
              className={`${selectClass} min-w-0 flex-1`}
            >
              <option value="">
                Select a {ENTITY_META[pickerType].label.toLowerCase()}…
              </option>
              {options[pickerType].map((o) => (
                <option key={o.id} value={o.id}>
                  {o.label}
                </option>
              ))}
            </select>
            <Button
              variant="outline"
              size="sm"
              onClick={onAddLink}
              disabled={!pickerId || link.isPending}
            >
              <Plus className="size-4" />
              Link
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Body */}
      <Card>
        <CardContent className="pt-6">
          {msg.body_text ? (
            <pre className="whitespace-pre-wrap break-words font-sans text-sm leading-relaxed text-foreground/90">
              {msg.body_text}
            </pre>
          ) : (
            <p className="text-sm text-muted-foreground">No text body.</p>
          )}
        </CardContent>
      </Card>

      {/* Attachments */}
      {msg.attachments.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Paperclip className="size-4" />
              Attachments
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {msg.attachments.map((a) => (
                <li key={a.id}>
                  <a
                    href={a.url ?? "#"}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-2 text-sm hover:text-primary"
                  >
                    <FileText className="size-4 text-muted-foreground" />
                    <span className="font-medium">{a.filename}</span>
                    <span className="text-xs text-muted-foreground">
                      {(a.size_bytes / 1024).toFixed(0)} KB
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
